/**
 * Оклады и ведомость — sv_control, за отдельным правом `salaries`.
 *
 * Всё здесь арендно-скоупленное, поэтому `requestControl()`/`requestControlRaw()` и
 * никогда `request()`.
 *
 * ## Правило про query-параметры, из-за которого этот файл не собирает их вручную
 *
 * **Тело** запроса принимает обе записи (`amountMinor` и `amount_minor` — одно и то
 * же). **Параметры строки запроса — только `camelCase`, синонимов нет.**
 * `?departmentIds=…` работает, `?department_ids=…` даёт `400`. Раньше незнакомый
 * параметр молча отбрасывался и запрос выполнялся БЕЗ ФИЛЬТРА — 26 августа 2026 на
 * этом был вычищен реестр машин (changelog `2026-08-26-1200`). Теперь это `400`, и
 * это лучше; но соседний `/control/attendance` по-прежнему ждёт `department_ids`, так
 * что две одинаково выглядящие подписи ниже расходятся ровно в этом месте — не
 * «унифицируйте» их.
 *
 * ## Право
 *
 * `salaries/read` на чтение, `salaries/create` и `salaries/delete` на запись — не
 * `personnel`. Карточку человека читает всякий с `personnel/read`, включая оператора
 * на проходной, и оклад ему видеть незачем. У ролей «руководитель отдела» и
 * «наблюдатель» доступ запрещён ЯВНО, так что `403` здесь — штатное состояние даже у
 * человека, который видит в компании всё остальное.
 */
import {requestControl, requestControlRaw} from '@/shared/api';
import {
  periodParams,
  requestPeriod,
  type Period,
} from '@/entities/attendance/period';
import type {ExportFormat} from '@/entities/attendance/api';
import type {
  PayrollReport,
  SalaryHistory,
  SalaryInput,
  SalaryList,
  SalaryRecord,
} from './types';

/** `camelCase`, потому что синонимов у параметров этих маршрутов нет — см. модуль. */
function payrollQuery(
  period: Period,
  extra: Record<string, string> = {},
): string {
  // Подрезано, а не проверено: к моменту сборки запроса у экрана уже была
  // возможность возразить, а `422` из-за ползунка, за которым никто не следил, хуже
  // ближайшего законного окна. Тот же приём, что в отчётах явки, — и та же обрезка
  // будущего: без неё колонка «Явка» показывала бы 19 из 21 двадцать шестого числа,
  // где двух пропусков ещё не случилось. На начисленное это не влияет: знаменатель у
  // оклада — плановые дни МЕСЯЦА, а не периода.
  return new URLSearchParams({
    ...periodParams(requestPeriod(period)),
    ...extra,
  }).toString();
}

/**
 * История ставок одного человека.
 *
 * `current` нормализуется к `null`: протобуф отдаёт ключ всегда, но у человека без
 * окладов там пусто, и `undefined` против `null` — разница, на которой экран однажды
 * покажет «оклад не задан» там, где он задан.
 */
export async function fetchSalaryHistory(
  companyId: string,
  recordId: string,
): Promise<SalaryHistory> {
  const res = await requestControl<Partial<SalaryHistory>>(
    `/control/personnel/${encodeURIComponent(recordId)}/salary`,
    companyId,
  );
  return {records: res.records ?? [], current: res.current ?? null};
}

/**
 * Объявить ставку.
 *
 * Правки существующей записи НЕТ и не будет: история отвечает на вопрос «что было
 * объявлено», а не «что мы потом переписали». Ошибочную удаляют и заводят заново, —
 * поэтому в интерфейсе рядом с записью стоит «удалить», а не «карандаш».
 *
 * Вторая запись на ту же дату — `409`, а не тихая замена: две правды об одном дне и
 * есть то, чего история не должна допускать. Текст ошибки показывайте как есть, он
 * объясняет, что делать.
 */
export function createSalary(
  companyId: string,
  recordId: string,
  input: SalaryInput,
): Promise<SalaryRecord> {
  return requestControl<SalaryRecord>(
    `/control/personnel/${encodeURIComponent(recordId)}/salary`,
    companyId,
    {method: 'POST', body: JSON.stringify(input)},
  );
}

/** Удалить одну запись истории. `404`, если её уже нет. */
export function deleteSalary(
  companyId: string,
  recordId: string,
  salaryId: string,
): Promise<void> {
  return requestControl<void>(
    `/control/personnel/${encodeURIComponent(recordId)}/salary/${encodeURIComponent(salaryId)}`,
    companyId,
    {method: 'DELETE'},
  );
}

export type CurrentSalariesParams = {
  /** Пусто — вся компания. Уходит через запятую в `departmentIds`. */
  departmentIds?: string[];
  /** `YYYY-MM-DD`; по умолчанию сервер берёт сегодня. */
  at?: string;
};

/**
 * Действующие ставки пачкой — один запрос вместо запроса на человека.
 *
 * ⚠️ **У кого ставки нет — строки нет вовсе**, а не строка с нулём. Список персонала
 * должен рисовать в такой ячейке прочерк и предложение завести оклад, а не «0».
 */
export async function fetchCurrentSalaries(
  companyId: string,
  params: CurrentSalariesParams = {},
): Promise<SalaryRecord[]> {
  const qs = new URLSearchParams();
  if (params.departmentIds?.length) {
    qs.set('departmentIds', params.departmentIds.join(','));
  }
  if (params.at) {
    qs.set('at', params.at);
  }
  const query = qs.toString();
  const res = await requestControl<Partial<SalaryList>>(
    query ? `/control/salaries?${query}` : '/control/salaries',
    companyId,
  );
  return res.records ?? [];
}

export type PayrollParams = {
  /** sv_control `department_id` — тот же id, которым ключуются отчёты явки. */
  departmentIds?: string[];
};

/**
 * Ведомость за период: сколько начислено по явке.
 *
 * Это НЕ расчётный лист. Отпусков, больничных, авансов, налогов и удержаний ведомость
 * не знает; пропуск — это неявка, и она не оплачивается. Экран, который это показывает,
 * обязан сказать это вслух — иначе бухгалтер примет одно за другое.
 */
export function fetchPayroll(
  companyId: string,
  period: Period,
  params: PayrollParams = {},
): Promise<PayrollReport> {
  const extra: Record<string, string> = {};
  if (params.departmentIds?.length) {
    extra.departmentIds = params.departmentIds.join(',');
  }
  return requestControl<PayrollReport>(
    `/control/payroll?${payrollQuery(period, extra)}`,
    companyId,
  );
}

/**
 * Та же ведомость файлом. Отдаёт `Response` с `Content-Disposition` —
 * `shared/lib/download` превращает его в сохранение на вебе.
 *
 * В файле деньги тоже целым в минорных единицах, а валюта — отдельной колонкой:
 * подставлять разделитель разрядов и знак валюты за бухгалтера значит угадывать его
 * формат, а он у каждого свой.
 */
export function exportPayroll(
  companyId: string,
  period: Period,
  format: ExportFormat = 'xlsx',
  /** Язык заголовков листа. Ведомость читает тот же, кто её просил. */
  lang?: string,
  params: PayrollParams = {},
): Promise<Response> {
  const extra: Record<string, string> = {format};
  if (params.departmentIds?.length) {
    extra.departmentIds = params.departmentIds.join(',');
  }
  if (lang) {
    extra.lang = lang;
  }
  return requestControlRaw(
    `/control/payroll/export?${payrollQuery(period, extra)}`,
    companyId,
  );
}
